import { CalendarClock } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, Dialog, Field } from "../../components/ui";
import { crmApi } from "../../data/crm";
import { mexicoDateKey } from "../../data/format";
import { DateTimeBox } from "../../components/DateTimeBox";

/** 09:00 Mexico City (UTC-6 all year since 2022), `days` after today. */
export function mexicoMorning(days: number, now = new Date()) {
  const today = mexicoDateKey(now);
  const day = new Date(Date.parse(`${today}T12:00:00Z`) + days * 86400000)
    .toISOString()
    .slice(0, 10);
  return new Date(`${day}T09:00:00-06:00`).toISOString();
}

const quickDates = [
  { label: "Mañana", days: 1 },
  { label: "En 2 días", days: 2 },
  { label: "En 1 semana", days: 7 },
] as const;

/**
 * "¿Qué sigue?": every open sale keeps exactly one next action with a date.
 * The server closes the current pending follow-up as `previous` before creating this one.
 */
export function NextActionDialog({
  open,
  opportunityId,
  customerName,
  previous = "cancelled",
  onClose,
  onSaved,
}: {
  open: boolean;
  opportunityId: string | null;
  customerName: string;
  previous?: "completed" | "cancelled";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [choice, setChoice] = useState<number | "custom">(1);
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setChoice(1);
    setCustom("");
    setNote("");
    setError("");
  }, [open, opportunityId]);
  const dueAt =
    choice === "custom"
      ? custom && Number.isFinite(Date.parse(custom))
        ? new Date(custom).toISOString()
        : ""
      : mexicoMorning(choice);
  const valid =
    Boolean(opportunityId) &&
    Boolean(note.trim()) &&
    Boolean(dueAt) &&
    Date.parse(dueAt) > Date.now();
  const save = async () => {
    if (!valid || busy || !opportunityId) return;
    setBusy(true);
    setError("");
    try {
      await crmApi.createFollowup({
        opportunity_id: opportunityId,
        due_at: dueAt,
        note: note.trim(),
        previous,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "No pudimos agendar la próxima acción.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      size="compact"
      title="¿Qué sigue?"
      eyebrow={customerName}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Nada por ahora
          </Button>
          <Button
            icon={CalendarClock}
            disabled={!valid || busy}
            onClick={() => void save()}
          >
            {busy ? "Agendando…" : "Agendar"}
          </Button>
        </>
      }
    >
      <p className="next-action-dialog__lead">
        Cada cliente tiene una sola próxima acción. Al agendarla, reemplaza la
        anterior.
      </p>
      <label className="quote-form-label">
        Qué vas a hacer
        <textarea
          value={note}
          maxLength={2000}
          rows={3}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ej. Llamar para confirmar la fecha del vuelo"
        />
      </label>
      <div className="funnel-chips" role="group" aria-label="Cuándo">
        {quickDates.map((item) => (
          <button
            key={item.days}
            type="button"
            className="funnel-chip"
            aria-pressed={choice === item.days}
            onClick={() => setChoice(item.days)}
          >
            {item.label}
          </button>
        ))}
        <button
          type="button"
          className="funnel-chip"
          aria-pressed={choice === "custom"}
          onClick={() => setChoice("custom")}
        >
          Otra fecha
        </button>
      </div>
      {choice === "custom" && (
        <DateTimeBox
          label="Fecha"
          name="next_action_at"
          value={custom}
          onChange={setCustom}
        />
      )}
      {choice !== "custom" && (
        <p className="automation-meta">A las 9:00 de la mañana.</p>
      )}
      {!opportunityId && (
        <p role="alert">
          Este expediente aún no tiene una venta registrada. Crea la consulta
          para poder agendarle seguimiento.
        </p>
      )}
      {error && <p role="alert">{error}</p>}
    </Dialog>
  );
}
